import { useEffect, useState, type FormEvent, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { Shield, KeyRound, Loader2, X, Smartphone, Copy, Check } from 'lucide-react'
import { toDataURL } from 'qrcode'
import { api } from '../../api/client.js'
import { useAuthStore, type User } from '../../store/authStore.js'

interface LoginMfaApiResponse {
  user: {
    id: string
    tenantId: string
    tenantSlug: string
    roles: string[]
    permissions: string[]
  }
  csrfToken: string
  backupCodes?: string[]
}

interface PlatformMfaApiResponse {
  platformAdmin: {
    id: string
    email: string
    tenantId: string
    tenantSlug: string
    roles: string[]
    permissions: string[]
    isSuperAdmin: boolean
  }
  csrfToken: string
  backupCodes?: string[]
}

export interface MfaSetupData {
  secret: string
  otpauthUrl: string
}

interface LoginMfaDialogProps {
  preAuthToken: string
  onClose: () => void
  mode?: 'tenant' | 'platform'
  setup?: MfaSetupData | null
  onSuccess?: (user: User) => void
}

export default function LoginMfaDialog({
  preAuthToken,
  onClose,
  mode = 'tenant',
  setup,
  onSuccess,
}: LoginMfaDialogProps): JSX.Element {
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)

  const isSetupMode = Boolean(setup)
  const supportsBackup = !isSetupMode
  const [mfaMode, setMfaMode] = useState<'totp' | 'backup'>('totp')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [secretCopied, setSecretCopied] = useState(false)
  const [codesCopied, setCodesCopied] = useState(false)
  const [savedBackupCodes, setSavedBackupCodes] = useState<string[] | null>(null)
  const [pendingUserSession, setPendingUserSession] = useState<{ user: User; csrfToken: string } | null>(null)

  useEffect(() => {
    if (setup?.otpauthUrl) {
      setQrDataUrl(null)
      toDataURL(setup.otpauthUrl, { width: 180, margin: 2 })
        .then((url: string) => setQrDataUrl(url))
        .catch(() => {})
    }
  }, [setup?.otpauthUrl])

  async function copySecret(): Promise<void> {
    if (!setup?.secret) return
    try {
      await navigator.clipboard.writeText(setup.secret)
      setSecretCopied(true)
      window.setTimeout(() => setSecretCopied(false), 2000)
    } catch {
      // ignore
    }
  }

  async function copyBackupCodes(): Promise<void> {
    if (!savedBackupCodes) return
    try {
      await navigator.clipboard.writeText(savedBackupCodes.join('\n'))
      setCodesCopied(true)
      window.setTimeout(() => setCodesCopied(false), 2000)
    } catch {
      // ignore
    }
  }

  function handleCompleteBackupStep(): void {
    if (!pendingUserSession) return
    setSession({
      user: { ...pendingUserSession.user, portal: mode === 'platform' ? 'admin' : 'agent' },
      csrfToken: pendingUserSession.csrfToken,
    })
    if (onSuccess) {
      onSuccess(pendingUserSession.user)
    } else {
      navigate('/')
    }
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (!code.trim()) {
      setError('Please enter a code')
      return
    }

    setError(null)
    setLoading(true)

    try {
      let user: User | null = null
      let csrfToken: string | null = null
      let newBackupCodes: string[] | undefined

      if (mode === 'platform') {
        const endpoint =
          mfaMode === 'totp' ? '/auth/platform/mfa/verify' : '/auth/platform/mfa/backup-code'
        const resp = await api.post<PlatformMfaApiResponse>(endpoint, {
          code: code.trim(),
          preAuthToken,
        })
        if (resp.platformAdmin && resp.csrfToken) {
          user = {
            ...resp.platformAdmin,
            sub: resp.platformAdmin.id,
            isSuperAdmin: resp.platformAdmin.isSuperAdmin,
          }
          csrfToken = resp.csrfToken
          newBackupCodes = resp.backupCodes
        }
      } else {
        const endpoint =
          mfaMode === 'totp' ? '/auth/mfa/verify-login' : '/auth/mfa/backup-code'
        const resp = await api.post<LoginMfaApiResponse>(endpoint, {
          code: code.trim(),
          preAuthToken,
        })
        if (resp.user && resp.csrfToken) {
          const u: User = { ...resp.user, sub: resp.user.id }
          try {
            const me = await api.get<{ user: User }>('/auth/me')
            if (me?.user) user = { ...me.user, sub: me.user.id }
            else user = u
          } catch {
            user = u
          }
          csrfToken = resp.csrfToken
          newBackupCodes = resp.backupCodes
        }
      }

      if (!user || !csrfToken) {
        setError('Unexpected response from server')
        return
      }

      // If backup codes were generated (first-time tenant MFA enrollment), display them first
      if (newBackupCodes && newBackupCodes.length > 0) {
        setPendingUserSession({ user, csrfToken })
        setSavedBackupCodes(newBackupCodes)
        return
      }

      setSession({ user: { ...user, portal: mode === 'platform' ? 'admin' : 'agent' }, csrfToken })
      if (onSuccess) {
        onSuccess(user)
      } else {
        navigate('/')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code. Please try again.')
    } finally {
      setLoading(false)
    }
  }


  // Backup codes presentation screen
  if (savedBackupCodes && savedBackupCodes.length > 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
        <div className="w-full max-w-md rounded-[3px] border border-gray-200 bg-white p-6 shadow-xl">
          <div className="mb-4 flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[3px] bg-blue-50 text-[#3366FF]">
              <KeyRound className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-[17px] font-bold leading-tight tracking-tight text-[#16273F]">
                Save Your Backup Codes
              </h3>
              <p className="mt-0.5 text-[12.5px] text-red-600 font-medium">
                Save these emergency codes in a safe place. They will not be shown again.
              </p>
            </div>
          </div>

          <pre className="max-h-56 overflow-auto rounded-[3px] border border-gray-200 bg-slate-50 p-3 text-sm font-mono text-gray-800">
            {savedBackupCodes.join('\n')}
          </pre>

          <div className="mt-5 flex gap-2">
            <button
              type="button"
              onClick={copyBackupCodes}
              className="flex items-center justify-center gap-1.5 rounded-[3px] border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              {codesCopied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              {codesCopied ? 'Copied' : 'Copy Codes'}
            </button>
            <button
              type="button"
              onClick={handleCompleteBackupStep}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-[3px] bg-[#3366FF] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2952CC]"
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-[3px] border border-gray-200 bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[3px] bg-[#EEF4FF] text-[#3366FF]">
              {isSetupMode ? (
                <Smartphone className="h-5 w-5" />
              ) : mfaMode === 'totp' ? (
                <Shield className="h-5 w-5" />
              ) : (
                <KeyRound className="h-5 w-5" />
              )}
            </span>
            <div>
              <h3 className="text-[17px] font-bold leading-tight tracking-tight text-[#16273F]">
                {isSetupMode
                  ? 'Set Up Multi-Factor Authentication'
                  : mfaMode === 'totp'
                    ? 'Two-Factor Authentication'
                    : 'Backup code'}
              </h3>
              <p className="mt-0.5 text-[12.5px] text-gray-500">
                {isSetupMode
                  ? 'MFA is mandatory for your account. Scan this code with your authenticator app.'
                  : mfaMode === 'totp'
                    ? 'Enter the 6-digit code from your authenticator app.'
                    : 'Enter one of your single-use backup codes.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[3px] p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-[3px] border-l-[3px] border-[#FF9933] bg-red-50 p-4">
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        {isSetupMode && setup && (
          <div className="mb-4 space-y-3">
            <div className="flex justify-center">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="MFA QR Code"
                  className="h-44 w-44 rounded-[3px] border border-gray-200 p-2"
                />
              ) : (
                <div className="flex h-44 w-44 items-center justify-center rounded-[3px] border border-gray-200 bg-gray-50">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              )}
            </div>

            <div>
              <p className="text-[12px] text-gray-500">
                Can't scan? Enter key manually:
              </p>
              <div className="mt-1 flex gap-2">
                <input
                  readOnly
                  value={setup.secret}
                  className="w-full rounded-[3px] border border-gray-300 bg-gray-50 px-3 py-1.5 font-mono text-xs text-gray-700 outline-none"
                />
                <button
                  type="button"
                  onClick={copySecret}
                  className="flex items-center gap-1 rounded-[3px] border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  aria-label="Copy key"
                >
                  {secretCopied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                  {secretCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="mfa-code"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              {isSetupMode
                ? 'Enter 6-digit code from authenticator app'
                : mfaMode === 'totp'
                  ? 'Authenticator code'
                  : 'Backup code'}
            </label>
            <input
              id="mfa-code"
              type="text"
              inputMode={mfaMode === 'totp' ? 'numeric' : 'text'}
              pattern={mfaMode === 'totp' ? '[0-9]*' : undefined}
              maxLength={mfaMode === 'totp' ? 6 : undefined}
              value={code}
              onChange={(e) =>
                setCode(
                  mfaMode === 'totp'
                    ? e.target.value.replace(/\D/g, '').slice(0, 6)
                    : e.target.value,
                )
              }
              className="w-full rounded-[3px] border border-gray-300 px-4 py-3 text-sm text-gray-900 outline-none transition-all placeholder:text-gray-400 focus:border-[#3366FF] focus:ring-2 focus:ring-[#3366FF]"
              placeholder={mfaMode === 'totp' ? '6-digit code' : 'Backup code'}
              autoFocus
              disabled={loading}
            />
          </div>
          <button
            type="submit"
            disabled={loading || !code.trim()}
            className="flex w-full items-center justify-center gap-2 rounded-[3px] bg-[#3366FF] py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#2952CC] focus:outline-none focus:ring-2 focus:ring-[#3366FF] focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : isSetupMode ? (
              <Check className="h-4 w-4" />
            ) : mfaMode === 'totp' ? (
              <Shield className="h-4 w-4" />
            ) : (
              <KeyRound className="h-4 w-4" />
            )}
            {isSetupMode ? 'Verify & Complete Sign In' : mfaMode === 'totp' ? 'Verify' : 'Use backup code'}
          </button>

        </form>

        {supportsBackup && (
          <div className="mt-5 text-center">
            <button
              type="button"
              onClick={() => {
                setMfaMode(mfaMode === 'totp' ? 'backup' : 'totp')
                setCode('')
                setError(null)
              }}
              className="text-sm font-medium text-[#3366FF] hover:text-[#2952CC]"
            >
              {mfaMode === 'totp' ? 'Use a backup code' : 'Use authenticator app'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
